import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NxAutocompleteModule } from '@aposin/ng-aquila/autocomplete';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxDatefieldModule, NxNativeDateModule, NxDateAdapter } from '@aposin/ng-aquila/datefield';
import { NxDropdownModule } from '@aposin/ng-aquila/dropdown';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NxTimefieldModule } from '@aposin/ng-aquila/timefield';
import { firstValueFrom, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { BaseConfig, EmailDistributionsUpdate, IntermediateCommunicationRequest } from '../../models/communication.model';
import { EnrichedIncident, IncidentEnvironment, IncidentStatusCode } from '../../models/incident.model';
import { ServiceConfig } from '../../models/service-config.model';
import { CommunicationService } from '../../services/communication.service';
import { IncidentDataService } from '../../services/incident-data.service';
import { PermissionService } from '../../services/permission.service';
import { MockDataService } from '../../services/mock-data.service';
import { combineDateTime, CustomDateAdapter, formatDateTime, onDateKeydown, onTimeInput, timeValidator, toParisDateAndTime } from '../../utils/date-utils';
import { multipleEmailValidator, parseEmails } from '../../utils/mail.utils';
import { isLocalEnvironment } from '../../utils/environment.utils';


@Component({
  selector: 'app-intermediate-communication-form',
  standalone: true,
  imports: [
    CommonModule,
    NxMessageModule,
    NxInputModule,
    NxButtonModule,
    NxFormfieldModule,
    NxDropdownModule,
    NxAutocompleteModule,
    NxIconModule,
    ReactiveFormsModule,
    NxDatefieldModule,
    NxNativeDateModule,
    NxTimefieldModule,
  ],
  providers: [
    { provide: NxDateAdapter, useClass: CustomDateAdapter }
  ],
  templateUrl: './intermediate-communication-form.component.html',
  styleUrl: './intermediate-communication-form.component.scss'
})



export class IntermediateCommunicationFormComponent implements OnInit, AfterViewInit {
  private formBuilder = inject(FormBuilder);
  private communicationService = inject(CommunicationService);
  private incidentDataService = inject(IncidentDataService);
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private mockDataService = inject(MockDataService);
  permissionService = inject(PermissionService);

  intermediateCommunicationForm: FormGroup | null = null;
  isSubmitting = false;
  isResolved = false;
  incidentLastUpdateDateTime: string | undefined = undefined;
  incidentLastUpdatedby: string | undefined = undefined;
  isDevMode = isLocalEnvironment();
  serviceConfig: ServiceConfig | undefined = undefined;
  serviceOptions: { id: string; name: string }[] = [];
  filteredServiceOptions: { id: string; name: string }[] = [];
  applicationOptions: { id: string; name: string }[] = [];
  featureOptions: { id: string; name: string }[] = [];
  selectedFeatures: { id: string; name: string; emails: string[] }[] = [];
  priorityOptions = [
    { value: 'P1', label: 'P1' },
    { value: 'P2', label: 'P2' },
    { value: 'P3', label: 'P3' },
    { value: 'P4', label: 'P4' }
  ];
  incidentEnvironmentOptions = Object.values(IncidentEnvironment).map(value => ({
    value,
    label: value
  }));
  minDate: Date = new Date();


  ngOnInit(): void {
    this.initForm();
    this.loadServiceOptions();
    this.loadIncidentData();
  }

  ngAfterViewInit(): void {
    const timeInputs = this.elementRef.nativeElement.querySelectorAll('nx-timefield input');
    timeInputs.forEach((input: HTMLInputElement) => {
      input.addEventListener('input', (event) => this.onTimeInput(event));
    });
  }

  initForm() {
    this.intermediateCommunicationForm = this.formBuilder.group({
      pagerDutyIncidentId: [null, Validators.required],
      to: [null, [Validators.required, this.multipleEmailValidator]],
      cc: [null, [this.multipleEmailValidator]],
      priority: [null, Validators.required],
      incidentEnvironmentCode: [null, Validators.required],
      service: [null, Validators.required],
      application: [null, Validators.required],
      features: [[]],
      communicationCounter: [1, [Validators.required, Validators.min(1)]],
      subject: [null, Validators.required],
      description: [null, Validators.required],
      impactedUsers: [null],
      rootCause: [null],
      startDate: [null, Validators.required],
      estimatedEndDate: [null, [this.minDateValidator]],
      estimatedEndTime: [null, [this.timeValidator]],
      serviceNowTicket: [null, [Validators.required, this.serviceNowTicketValidator]],
      jiraTicketKey: [null],
      nextCommunication: [null],
      ongoingActions: [null],
      workaround: [null],
      technicalInformation: [null]
    });

    this.intermediateCommunicationForm.valueChanges.subscribe(() => {
      this.updateSubject();
    });

    this.intermediateCommunicationForm.get('service')?.valueChanges.subscribe((serviceId) => {
      this.onServiceChange(serviceId);
    });

    this.intermediateCommunicationForm.get('application')?.valueChanges.subscribe((applicationId) => {
      this.onApplicationChange(applicationId);
    });

    this.intermediateCommunicationForm.get('features')?.valueChanges.subscribe((featureIds) => {
      this.onFeaturesChange(featureIds);
    });

    this.intermediateCommunicationForm.get('estimatedEndDate')?.valueChanges.subscribe(() => {
      this.updateTimeValidation();
    });
    this.intermediateCommunicationForm.get('estimatedEndTime')?.valueChanges.subscribe(() => {
      this.updateTimeValidation();
    });

    this.intermediateCommunicationForm.get('pagerDutyIncidentId')?.disable();
    // Only disable service field if NOT in local development mode
    if (!isLocalEnvironment()) {
      this.intermediateCommunicationForm.get('service')?.disable();
    }
    this.intermediateCommunicationForm.get('communicationCounter')?.disable();
    this.intermediateCommunicationForm.get('startDate')?.disable();
    this.intermediateCommunicationForm.get('subject')?.disable();

    this.updateSubject();
  }

  multipleEmailValidator = (control: AbstractControl): ValidationErrors | null => {
    return multipleEmailValidator(control.value);
  }

  serviceNowTicketValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const serviceNowTicketRegex = /^INC\d+$/;
    
    if (!serviceNowTicketRegex.test(control.value)) {
      return { invalidServiceNowTicket: true };
    }

    return null;
  }

  timeValidator = (control: AbstractControl): ValidationErrors | null => {
    return timeValidator(control.value);
  }

  minDateValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return { minDate: true };
    }
    
    return null;
  }

  updateTimeValidation() {
    const dateControl = this.intermediateCommunicationForm?.get('estimatedEndDate');
    const timeControl = this.intermediateCommunicationForm?.get('estimatedEndTime');
    const requiredValidators = this.isResolved ? [Validators.required] : [];

    timeControl?.setValidators([...requiredValidators, this.timeValidator]);
    dateControl?.setValidators([...requiredValidators, this.minDateValidator]);
    
    timeControl?.updateValueAndValidity({ emitEvent: false });
    dateControl?.updateValueAndValidity({ emitEvent: false });
  }

  onTimeInput(event: Event): void {
    const timeControl = this.intermediateCommunicationForm?.get('estimatedEndTime');
    onTimeInput(event, timeControl || undefined);
  }

  onDateKeydown(event: KeyboardEvent): void {
    onDateKeydown(event);
  }

  updateSubject() {
    if (this.intermediateCommunicationForm) {
      const serviceConfig = this.serviceConfig
      const serviceName = serviceConfig?.pagerDutyServiceName || '{{service}}';
      const priority = this.intermediateCommunicationForm.get('priority')?.value || '{{Priority}}';
      const incidentEnvironmentCode = this.intermediateCommunicationForm.get('incidentEnvironmentCode')?.value || '{{Environment}}';
      const counter = this.intermediateCommunicationForm.get('communicationCounter')?.value || 'Communication counter';

      const subject = `${serviceName}[${incidentEnvironmentCode}][${priority}] Incident Ongoing - Communication ${counter}`;
      this.intermediateCommunicationForm.get('subject')?.setValue(subject, { emitEvent: false });
    }
  }

  loadServiceOptions() {
    const incident = this.incidentDataService.getIncidentData();

    if (incident && incident.service) {
      this.serviceConfig = incident.service;
      this.serviceOptions = [{
       id: incident?.service?.pagerDutyServiceId,
        name: incident?.service?.pagerDutyServiceName
      }]
    }

    if (isLocalEnvironment()) {
      this.mockDataService.getServiceConfigs().subscribe({
        next: (response) => {
          if (response.body) {
            this.serviceOptions = response.body.map(config => ({
              id: config.pagerDutyServiceId,
              name: config.pagerDutyServiceName
            }));
          }
        },
        error: (error) => {
          console.error('Error loading services:', error);
        }
      });
    }
  }

  loadIncidentData(): void {
    const incident = this.incidentDataService.getIncidentData();
    if (incident && this.intermediateCommunicationForm) {
        this.isResolved = incident.incidentStatusCode === IncidentStatusCode.RESOLVED;
        this.incidentLastUpdateDateTime = incident.lastUpdateDateTime;
        this.incidentLastUpdatedby = incident.lastUpdatedby;
        this.applyEstimatedEndValidators();
        this.prefillFormWithIncident(incident);
    }
  }

  private applyEstimatedEndValidators(): void {
    const dateControl = this.intermediateCommunicationForm?.get('estimatedEndDate');
    const timeControl = this.intermediateCommunicationForm?.get('estimatedEndTime');

    if (this.isResolved) {
      dateControl?.setValidators([this.minDateValidator, Validators.required]);
      timeControl?.setValidators([this.timeValidator, Validators.required]);
    } else {
      dateControl?.setValidators([this.minDateValidator]);
      timeControl?.setValidators([this.timeValidator]);
    }

    dateControl?.updateValueAndValidity({ emitEvent: false });
    timeControl?.updateValueAndValidity({ emitEvent: false });
  }

  prefillFormWithIncident(incident: EnrichedIncident): void {
    if (!this.intermediateCommunicationForm) return;

    // Stored in UTC, shown in Paris time
    const { date: estimatedEndDate, time: estimatedEndTime } = toParisDateAndTime(incident.incidentEndDateTime);

    const formData = {
      pagerDutyIncidentId: incident.pagerDutyIncidentId || null,
      priority: incident.priorityCode || null,
      incidentEnvironmentCode: incident.incidentEnvironmentCode || null,
      service: incident.pagerDutyServiceId || null,
      communicationCounter: incident.incidentCommunicationCounter || 1,
      description: incident.incidentDescription || null,
      impactedUsers: incident.impactedUserDescription || (incident.service?.pagerDutyServiceName ? `All users of ${incident.service.pagerDutyServiceName}` : null),
      rootCause: incident.rootCauseDescription || null,
      startDate: incident.incidentStartDateTime ? formatDateTime(incident.incidentStartDateTime) : null,
      estimatedEndDate: estimatedEndDate,
      estimatedEndTime: estimatedEndTime,
      serviceNowTicket: incident.serviceIntegrationManagementTicketId || null,
      jiraTicketKey: incident.jiraTicketKey || null,
      ongoingActions: incident.ongoingActionsDescription || null,
      workaround: incident.workaroundDescription || null,
      technicalInformation: incident.technicalInformationDescripion || null,
      cc: incident.carbonCopies?.join(', ') || "",
      to: ""
    };

    this.intermediateCommunicationForm.patchValue(formData);

    if (isLocalEnvironment()) {
      this.intermediateCommunicationForm.get('service')?.enable({ emitEvent: false });
    }

    if (formData.service) {
      this.loadApplicationsForService()
       if (incident.impactedApplications && incident.impactedApplications.length > 0) {
          const firstApp = incident.impactedApplications[0];
          const applicationId = firstApp.applicationId;

          if (applicationId) {
            this.intermediateCommunicationForm?.patchValue({ application: applicationId });

            this.loadFeaturesForApplication(applicationId)

            if (firstApp.impactedFeatureIds && firstApp.impactedFeatureIds.length > 0) {
              this.intermediateCommunicationForm?.patchValue({ features: firstApp.impactedFeatureIds });
            }
          }
        }
    }
  }

  onServiceChange(serviceId: string) {
    this.resetApplicationAndFeature();

    if (!serviceId) return;

    // In dev mode, load the selected service config from mock data
    if (isLocalEnvironment()) {
      this.mockDataService.getServiceConfig(serviceId).subscribe({
        next: (response) => {
          if (response.body) {
            this.serviceConfig = response.body;
            this.updateEmailFields(this.serviceConfig?.emailDistributionList);
            this.loadApplicationsForService();
          }
        },
        error: (error) => {
          console.error('Error loading service config:', error);
        }
      });
    } else {
      const selectedService = this.serviceConfig;
      this.updateEmailFields(selectedService?.emailDistributionList);
      this.loadApplicationsForService();
    }
  }

  private resetApplicationAndFeature() {
    this.intermediateCommunicationForm?.get('application')?.setValue(null, { emitEvent: false });
    this.intermediateCommunicationForm?.get('features')?.setValue([], { emitEvent: false });
    this.applicationOptions = [];
    this.featureOptions = [];
    this.selectedFeatures = [];
    this.removeFeatureToFields();
  }

  private updateEmailFields(distributionList?: string[]) {
    if (!distributionList || distributionList.length === 0) return;

    this.intermediateCommunicationForm?.get('to')?.setValue(distributionList.join(', '));
  }

  loadApplicationsForService(): void {
    this.applicationOptions = this.serviceConfig?.applications?.map(app => ({
      id: app.applicationId,
      name: app.applicationName
    })) || [];

  }

  onApplicationChange(applicationId: string) {
    this.intermediateCommunicationForm?.get('features')?.setValue([], { emitEvent: false });
    this.featureOptions = [];
    this.selectedFeatures = [];
    this.removeFeatureToFields();

    if (!applicationId) return;

    const serviceId = this.intermediateCommunicationForm?.get('service')?.value;
    const selectedService = this.serviceConfig;
    const selectedApplication = selectedService?.applications?.find(app => app.applicationId === applicationId);

    this.updateEmailFields(selectedApplication?.emailDistributions);

    if (serviceId) {
      this.loadFeaturesForApplication(applicationId);
    }
  }

  loadFeaturesForApplication(applicationId: string): void {
    const foundApplication = this.serviceConfig?.applications?.find(app => app.applicationId === applicationId)
    this.featureOptions = foundApplication?.features?.map(feature => ({
      id: feature.featureId,  
      name: feature.featureName
    })) || [];
  }

  onFeaturesChange(featureIds: string[]) {
    if (!featureIds || featureIds.length === 0) {
      this.selectedFeatures = [];
      this.removeFeatureToFields();
      return;
    }

    const applicationId = this.intermediateCommunicationForm?.get('application')?.value;
    const selectedService = this.serviceConfig;
    const selectedApplication = selectedService?.applications?.find(app => app.applicationId === applicationId);

    this.selectedFeatures = featureIds.map(featureId => {
      const feature = selectedApplication?.features?.find(f => f.featureId === featureId);
      return {
        id: featureId,
        name: feature?.featureName || featureId,
        emails: feature?.emailDistributions || []
      };
    });

    if (this.selectedFeatures.length === 1) {
      this.removeFeatureToFields();
      this.updateEmailFields(this.selectedFeatures[0].emails);
    } else if (this.selectedFeatures.length > 1) {
      this.removeFeatureToFields();
      this.createFeatureToFields();
    }
  }

  private createFeatureToFields() {
    this.selectedFeatures.forEach(feature => {
      const controlName = `to_${feature.id}`;
      if (!this.intermediateCommunicationForm?.get(controlName)) {
        this.intermediateCommunicationForm?.addControl(
          controlName,
          this.formBuilder.control(feature.emails.join(', '), [Validators.required, this.multipleEmailValidator])
        );
      } else {
        this.intermediateCommunicationForm?.get(controlName)?.setValue(feature.emails.join(', '));
      }
    });
  }

  private removeFeatureToFields() {
    Object.keys(this.intermediateCommunicationForm?.controls || {}).forEach(key => {
      if (key.startsWith('to_')) {
        this.intermediateCommunicationForm?.removeControl(key);
      }
    });
  }

  async onSubmit() {
    if (this.isSubmitting || !this.intermediateCommunicationForm?.valid) return;

    this.isSubmitting = true;
    const formData = this.intermediateCommunicationForm?.getRawValue();

    try {
      const communicationRequest = this.buildCommunicationRequest(formData);
      await this.sendCommunication(communicationRequest);
    } catch (error) {
      alert('Error processing form data. Please try again.' + error);
      this.isSubmitting = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildCommunicationRequest(formData: any): IntermediateCommunicationRequest {
    const impactedApplications = this.buildImpactedApplications(formData);
    const emailDistributions = this.buildEmailDistributions(formData);

    return {
      pagerDutyIncidentId: formData.pagerDutyIncidentId,
      incidentUpdate: {
        incidentDescription: formData.description || undefined,
        startDateTime: formData.startDate || undefined,
        priorityCode: formData.priority || undefined,
        incidentEnvironmentCode: formData.incidentEnvironmentCode || undefined,
        serviceIntegrationManagementTicketId: formData.serviceNowTicket || undefined,
        jiraTicketKey: formData.jiraTicketKey || undefined,
        impactedApplications: impactedApplications.length > 0 ? impactedApplications : undefined,
        ongoingActionsDescription: formData.ongoingActions || undefined,
        workaroundDescription: formData.workaround || undefined,
        technicalInformationDescripion: formData.technicalInformation || undefined,
        rootCauseDescription: formData.rootCause || undefined,
        impactedUserDescription: formData.impactedUsers || undefined,
        // Always sent (empty when cleared) so removing all CCs is saved on the incident
        carbonCopies: parseEmails(formData.cc || '')
      },
      serviceConfigUpdate: emailDistributions.length > 0 ? {
        emailDistributions: emailDistributions
      } : undefined,
      incidentCommunicationSpecificInfo: {
        nextCommunicationText: formData.nextCommunication || undefined,
        incidentEstimatedEndDateTime: combineDateTime(formData.estimatedEndDate, formData.estimatedEndTime)
      }
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildImpactedApplications(formData: any) {
    const applicationId = formData.application || '';
    const featureIds = formData.features || [];

    const selectedApplication = this.applicationOptions.find(a => a.id === applicationId);

    const impactedFeatureNames: string[] = [];
    const impactedFeatureIds: string[] = [];

    featureIds.forEach((featureId: string) => {
      const feature = this.featureOptions.find((f) => f.id === featureId);
      if (feature) {
        impactedFeatureNames.push(feature.name);
        impactedFeatureIds.push(feature.id);
      }
    });

    return [{
      applicationId: applicationId,
      applicationName: selectedApplication?.name,
      impactedFeatureNames: impactedFeatureNames.length > 0 ? impactedFeatureNames : [],
      impactedFeatureIds: impactedFeatureIds.length > 0 ? impactedFeatureIds : []
    }];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildEmailDistributions(formData: any): EmailDistributionsUpdate[] {
    const emailDistributions: EmailDistributionsUpdate[] = [];
    const featureIds = formData.features || [];
    
    const baseConfig: BaseConfig = {
      pagerDutyServiceId: formData.service,
      applicationId: formData.application || '',
    };

    if (featureIds.length > 1) {
      featureIds.forEach((featureId: string) => {
        const toFieldName = `to_${featureId}`;
        const toValue = formData[toFieldName];
        
        if (toValue) {
          const featureConfig = { ...baseConfig, featureId };
          this.addEmailsToDistributions(toValue, emailDistributions, featureConfig);
        }
      });
    } else {
      if (featureIds.length === 1) {
        baseConfig.featureId = featureIds[0];
      }
      this.addEmailsToDistributions(formData.to, emailDistributions, baseConfig);
    }

    return emailDistributions;
  }

  private addEmailsToDistributions(
    emailString: string | null,
    emailDistributions: EmailDistributionsUpdate[],
    baseConfig: BaseConfig
  ) {
    if (!emailString) return;

    const emails = parseEmails(emailString);
    emails.forEach(email => {
      emailDistributions.push({
        emailDistribution: email,
        ...baseConfig
      });
    });
  }

  private async sendCommunication(communicationRequest: IntermediateCommunicationRequest) {
    const response = await firstValueFrom(
      this.communicationService.sendIntermediateCommunication(communicationRequest)
        .pipe(
          take(1),
          catchError((error) => {
            const errorMessage = this.getErrorMessage(error.status);
            alert(errorMessage);
            this.isSubmitting = false;
            return of(null);
          })
        )
    );
    this.isSubmitting = false;
    this.handleSuccessResponse(response);
  }

  private getErrorMessage(status: number): string {
    const errorMessages: Record<number, string> = {
      400: 'Invalid data provided. Please check all required fields.',
      500: 'Server error. Please try again later or contact support.'
    };
    return errorMessages[status] || 'Error sending communication. Please try again.';
  }

  private handleSuccessResponse(response: unknown) {
    if (!response) return;

    alert('Communication sent successfully!');
    this.intermediateCommunicationForm?.reset();
    this.updateSubject();
    this.router.navigate(['/incident-communications']);
  }

}
